import { useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { navigation } from '@/data/company';
import { Button } from './Button';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
}

export function MobileMenu({ isOpen, onClose }: MobileMenuProps) {
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <motion.div
      className="fixed inset-x-0 bottom-0 top-[4.6rem] z-40 border-t border-border bg-background px-4 pb-8 pt-5 lg:hidden"
      initial={reduceMotion ? false : { opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={reduceMotion ? undefined : { opacity: 0, y: -12 }}
      transition={{ duration: 0.22 }}
      aria-label="Mobilna navigacija"
    >
      <nav
        className="site-container flex h-full flex-col"
        aria-label="Glavna mobilna navigacija"
      >
        <div className="flex flex-col">
          {navigation.map((item, index) => (
            <motion.div
              key={item.to}
              initial={reduceMotion ? false : { opacity: 0, x: -12 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: reduceMotion ? 0 : index * 0.045 }}
            >
              <NavLink
                className={({ isActive }) =>
                  `animated-link flex items-center justify-between border-b border-border py-4 text-xl font-medium transition-colors ${isActive ? 'is-active text-primary' : 'text-text-primary hover:text-primary'}`
                }
                onClick={onClose}
                to={item.to}
              >
                {item.label}
                <ArrowUpRight aria-hidden="true" className="size-5" />
              </NavLink>
            </motion.div>
          ))}
        </div>
        <div className="mt-auto pt-8">
          <Button to="/kontakt" size="lg" fullWidth onAction={onClose}>
            Započnimo razgovor
            <ArrowUpRight aria-hidden="true" className="size-4" />
          </Button>
          <p className="mt-5 text-center text-sm text-text-muted">
            Odgovaramo u roku od jednog radnog dana.
          </p>
        </div>
      </nav>
    </motion.div>
  );
}
